import React, {useContext, useEffect, useState} from "react";
import {View, StyleSheet, TouchableOpacity} from "react-native";
import PtText from "../../core/pt-text/pt-text";
import PtOrderStatusIndicator from "./../pt-order-status-indicator/pt-order-status-indicator";
import {styleVars} from "../../../app-style-vars";
import PtIcon from "../../core/pt-icon/pt-icon";
import PtActivityIndicator from "../../core/pt-activity-indicator/pt-activity-indicator";
import {GetOrderDetailsRequest} from "../../../model/GetOrderDetailsRequest";
import {getOrderDetail} from "../../../actions/orderActions";
import {useDispatch, useSelector} from "react-redux";
import PtOrderPriceDetails from "./../pt-order-price-details/pt-order-price-details";
import PtOrderHistoryProduct from "./../pt-order-history-product/pt-order-history-product";
import {getTranslation} from "../../../helpers/getTranslation";
import PtCallout from "../../core/pt-callout/pt-callout";
import {PtOrderSingleProps} from './pt-order-single-props';
import {styles} from './pt-order-single-styles';
import UserContext from "../../../helpers/UserContext";

export default function PtOrderSingle(props: PtOrderSingleProps) {

  /** labels */
  const showProductsLabel = props.showProductsLabel || getTranslation('order.history.show.products.label') || 'Show all products in this order';
  const hideProductsLabel = props.hideProductsLabel || getTranslation('order.history.hide.products.label') || 'Hide all products in this order';
  const detailsLabel = props.detailsLabel || getTranslation('order.history.details.label') || 'Details';
  const paymentConditionsLabel = props.paymentConditionsLabel || getTranslation('order.history.payment.conditions.label') || 'Payment conditions';
  const shipToLabel = props.shipToLabel || getTranslation('order.history.ship.to.label') || 'Ship to';
  const errorMessage = props.errorMessage || getTranslation('order.history.order.details.error.message') || 'Error occured';

  /** data */
  const order = props.order;

  /** settings */
  const {userData, updateUserData} = useContext(UserContext);
  const [orderDetailsData, setOrderDetailsData] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [detailsCollected, setDetailsCollected] = useState(false);
  const [showShippingDetails, setShowShippingDetails] = useState(false);
  const [showProducts, setShowProducts] = useState(true);
  const [priceDetailsVisible, setPriceDetailsVisible] = useState(false);
  const [errors, setErrors] = useState(false);

  /** request params */
  const PTBaseSiteUID = props.baseSiteUID || userData.baseSiteUID;
  const PTSalesAreaDependantCustomerUid = props.salesAreaDependantCustomerUid || userData.salesAreaDependantCustomerUid;
  const currentLanguage = useSelector((state:any) => state.translationsReducer.currentLanguage);
  const dispatch = useDispatch();

  const shouldCollectDetailsData = () => {
    return !detailsCollected && showDetails;
  }

  useEffect(() => {
    if(shouldCollectDetailsData()){
      const orderDetailsRequest = new GetOrderDetailsRequest(order.orderNumber, order.scenarioId);
      getOrderDetail(orderDetailsRequest, dispatch, PTBaseSiteUID, PTSalesAreaDependantCustomerUid, currentLanguage)
        .then((res) => {
          if(!res.errors) {
            setOrderDetailsData(res);
          } else {
            setErrors(true);
          }
        })
        .finally(() => {
        setDetailsCollected(true);
      });
    }
  },[shouldCollectDetailsData()]);

  const toggleDetails = () => {
    setShowDetails(!showDetails);
  }

  const toggleShippingDetails = () => {
    setShowShippingDetails(!showShippingDetails);
  }

  const toggleProducts = () => {
    setShowProducts(!showProducts)
  }

  const priceDetailsHandler = (newValue:boolean) => {
    setPriceDetailsVisible(newValue)
  }

  return (
    <View style={[styles.orderContainer]}>
      <TouchableOpacity onPress={() => toggleDetails()}>
        <View style={[styles.orderHeaderContainer]}>
          <View style={[styles.openIndicator]}>
            <PtIcon iconName={showDetails ? 'less-minimize' : 'add'} iconSize={24} iconColor={styleVars.boschDarkBlue}/>
          </View>
          <View style={[styles.headerDataContainer]}>
            <View style={[styles.headerDataColumn]}>
              <PtText style={[styles.headerDataValueText, styles.orderNumberText]}>{order.orderNumber}</PtText>
              <PtText style={[styles.headerDataValueText]}>{order.purchaseOrderNumber}</PtText>
            </View>
            <View style={[styles.headerDataColumn, styles.headerDataColumnMiddle]}>
              <PtText style={[styles.headerDataValueText]}>{order.placedDate}</PtText>
              <PtText style={[styles.headerDataValueText]}>{order.totalAmount}</PtText>
            </View>
            <View style={[styles.headerDataColumn, styles.headerDataColumnRight]}>
              <PtOrderStatusIndicator statusCode={order.statusCode} />
            </View>
          </View>
        </View>
      </TouchableOpacity>
      {showDetails ?
        !errors ?
          <View style={[styles.orderDetailsContainer]}>
            {!detailsCollected ? <PtActivityIndicator show={true}/> : null}

            <View style={[styles.orderDetailsTitle]}>
              <PtText>{detailsLabel}</PtText>
            </View>

            <View style={[styles.paymentConditionsRow]}>
              <PtText style={[styles.paymentConditionsTitle]}>{paymentConditionsLabel}</PtText>
              <PtText style={[styles.paymentConditionsValue]}>{order.paymentCondition}</PtText>
            </View>

            <View style={[styles.shipToHeader]}>
              <TouchableOpacity onPress={() => toggleShippingDetails()}>
                <PtText style={[styles.shipToTitle]}>
                  <PtIcon iconName={ showShippingDetails ? 'up' : 'down' } style={[styles.shipToTitleIcon]}/>
                  {shipToLabel}
                </PtText>
              </TouchableOpacity>
            </View>

            {showShippingDetails && orderDetailsData?.shipTo ?
              <View style={[styles.shipToContainer]}>
                <PtText style={[styles.shipToText]}>{orderDetailsData?.shipTo?.firstName} {orderDetailsData?.shipTo?.lastName}</PtText>
                <PtText style={[styles.shipToText]}>{orderDetailsData?.shipTo?.streetName} {orderDetailsData?.shipTo?.streetNumber}</PtText>
                <PtText style={[styles.shipToText]}>{orderDetailsData?.shipTo?.postalCode} {orderDetailsData?.shipTo?.city}</PtText>
              </View>
              : null
            }

            <View>
              <PtOrderPriceDetails
                pricingData={orderDetailsData?.boschERPPricings}
                detailsVisibleHandler={priceDetailsHandler}
                detailsVisible={priceDetailsVisible}
              />
            </View>

            <View style={[styles.orderProductsToggleContainer]}>
              <TouchableOpacity onPress={() => toggleProducts()}>
                {showProducts ?
                  <PtText style={[styles.toggleProductsText]}>
                    <PtIcon iconName={'up'} style={[styles.toggleProductsText]}/>
                    {hideProductsLabel}
                  </PtText>
                  :
                  <PtText style={[styles.toggleProductsText]}>
                    <PtIcon iconName={'down'} style={[styles.toggleProductsText]}/>
                    {showProductsLabel}
                  </PtText>
                }
              </TouchableOpacity>
            </View>

            {showProducts ?
              <View style={[styles.orderProductsContainer]}>
                {orderDetailsData?.orderItems.map((orderItem: any, idx: number) => (
                  <View key={`orderItem-${idx}`}>
                    <PtOrderHistoryProduct
                      productData={orderItem}
                      order={order}
                    />
                  </View>
                ))}
              </View>
              : null
            }
          </View>
          :
          <PtCallout type={'error'} message={errorMessage}/>
        : null
      }
    </View>
  )
}
